import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { UserDialogs } from "@/components/users/UserDialogs";
import { UserTable, LoginAuditTable } from "@/components/users/UserTables";
import { CreateStaffDialog } from "@/components/users/CreateStaffDialog";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", email: "", password: "", role: "worker", department: "", manager_id: "", employee_id: "", mobile: "", designation: "", pin: "", branch: "", plant: "", shift: "", joining_date: "" };

const errMsg = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [auditState, setAuditState] = useState("idle");
  const [tab, setTab] = useState("users");
  const [open, setOpen] = useState(false);
  const [nextEmp, setNextEmp] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [pinTarget, setPinTarget] = useState(null);
  const [pinVal, setPinVal] = useState("");
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [roles, setRoles] = useState([]);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [openItems, setOpenItems] = useState(null);
  const [reassignTo, setReassignTo] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch {
      setError("You do not have permission to view user management.");
    }
    api.get("/rbac/roles").then((r) => setRoles(r.data.filter((x) => x.status === "active"))).catch(() => setRoles([]));
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditState("loading");
    try {
      const res = await api.get("/users/login-audit/list");
      setAuditRows(res.data);
      setAuditState("ok");
    } catch {
      setAuditRows([]);
      setAuditState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, loadAudit]);

  const openCreate = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      try {
        const r = await api.get("/users/next-employee-id");
        setNextEmp(r.data.employee_id);
      } catch { setNextEmp(""); }
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const changeStatus = async (userId, status) => {
    try {
      const res = await api.patch(`/users/${userId}/status`, { status });
      const openTasks = res.data?.open_tasks ?? 0;
      toast.success(status === "active" ? "Account activated"
        : `Account ${status} — access blocked, sessions revoked${openTasks ? `. ${openTasks} open task(s) need reassignment` : ""}`);
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    load();
  };

  const openReassign = async (u) => {
    setReassignTarget(u);
    setReassignTo("");
    setOpenItems(null);
    try {
      const r = await api.get(`/users/${u.user_id}/open-items`);
      setOpenItems(r.data);
    } catch { setOpenItems({ ops_tasks: [], office_tasks: [], total_open: 0 }); }
  };

  const submitReassign = async () => {
    if (!reassignTo) { toast.error("Select a user to reassign to"); return; }
    try {
      const r = await api.post(`/users/${reassignTarget.user_id}/reassign`, { to_user_id: reassignTo });
      toast.success(`${r.data.reassigned} task(s) reassigned to ${r.data.to}`);
      setReassignTarget(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Reassign failed")); }
  };

  const createStaff = async () => {
    try {
      await api.post("/users/staff", form);
      toast.success(`Account created for ${form.email}`);
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to create account")); }
  };

  const submitReset = async () => {
    try {
      await api.patch(`/users/${resetTarget.user_id}/password`, { password: resetPw });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setResetPw("");
    } catch (e) { toast.error(errMsg(e, "Failed to reset password")); }
  };

  const submitPin = async () => {
    try {
      await api.patch(`/users/${pinTarget.user_id}/pin`, { pin: pinVal });
      toast.success(`PIN set for ${pinTarget.name} — they can now sign in with ${pinTarget.employee_id || "their Employee ID"}`);
      setPinTarget(null);
      setPinVal("");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed to set PIN")); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Terminate login access for ${u.email}? Identity and audit history remain preserved.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`${u.email} terminated; history is preserved.`);
      load();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      {error && <p className="text-sm text-signal-red" data-testid="users-error">{error}</p>}
      <SectionTitle right={<CreateStaffDialog open={open} onOpenChange={openCreate} form={form} setForm={setForm} roles={roles} users={users} nextEmp={nextEmp} onSubmit={createStaff} />}>
        User & Role Management
      </SectionTitle>

      <div className="flex gap-1 border-b border-border" data-testid="users-tabs">
        {[["users", "User Master"], ["audit", "Login Activity"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <UserTable users={users} roles={roles} me={me} onRole={changeRole} onStatus={changeStatus}
          onReset={(u) => { setResetTarget(u); setResetPw(""); }} onPin={(u) => { setPinTarget(u); setPinVal(""); }}
          onReassign={openReassign} onDelete={deleteUser} />)}
      {tab === "audit" && <LoginAuditTable rows={auditRows} state={auditState} />}

      <p className="text-xs text-muted-foreground">WIPPLOPS is a closed system — only admin-created users can sign in. Google sign-in works only for emails pre-registered here (unknown emails are denied). Factory staff sign in with Employee ID + PIN. Suspend ex-employees instead of deleting — their history stays intact.</p>

      <UserDialogs
        resetTarget={resetTarget} setResetTarget={setResetTarget} resetPw={resetPw} setResetPw={setResetPw} submitReset={submitReset}
        pinTarget={pinTarget} setPinTarget={setPinTarget} pinVal={pinVal} setPinVal={setPinVal} submitPin={submitPin}
        reassignTarget={reassignTarget} setReassignTarget={setReassignTarget} openItems={openItems}
        reassignTo={reassignTo} setReassignTo={setReassignTo} submitReassign={submitReassign}
        users={users}
      />
    </div>
  );
}
